import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import {
  Plus, Edit2, Trash2, Power, Upload, QrCode, Image as ImageIcon,
  CreditCard, Zap, Link2, Copy, CheckCircle2, Key, Globe, Sparkles, Loader2, XCircle,
} from 'lucide-react';
import { resolvePaymentBrandLogo, resolvePaymentLogoShape } from '../../lib/paymentBrands';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const normalizeGatewayKey = (s) => slugify(s).replace(/_/g, '');
const LINKGEN_METHOD_ALIASES = new Set(['cashapp', 'applepay', 'googlepay', 'paypal', 'btc', 'bitcoin']);
const LINKGEN_CALLBACK_ALIASES = {
  cashapp: 'cashapp',
  applepay: 'applepay',
  googlepay: 'googlepay',
  paypal: 'paypal',
  btc: 'btc',
  bitcoin: 'btc',
};

const EMPTY = {
  payment_mode: 'LOCAL_MANUAL',        // LOCAL_MANUAL | GATEWAY
  title: '',
  flow: 'both',
  method_details: '',
  bonus_percent: 0,
  fee_percent: 0,
  min_amount: 10,
  max_amount: 5000,
  receiving_address: '',
  admin_note: '',
  logo_url: '',
  enabled: true,
  // Gateway-only fields:
  provider_code: '',
  gateway_integration_id: null,         // set on edit so we know which integration to update
  base_url: '',
  api_key: '',
  api_secret: '',
};

const resolveStoredLogo = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/payment-logos/')) return url;
  return `${BACKEND_URL}${url}`;
};

const paymentLogoFor = (method) => {
  const uploaded = method?.logo_url;
  if (uploaded) return uploaded.startsWith('http') ? uploaded : `${BACKEND_URL}${uploaded}`;
  return resolvePaymentBrandLogo(method);
};

const PaymentLogoPlate = ({ src, method, alt = 'Payment logo', size = 'md' }) => {
  if (!src) return null;
  const shape = resolvePaymentLogoShape(src, method);
  const plate = size === 'sm'
    ? (shape === 'wordmark' ? 'h-10 w-[76px]' : 'h-10 w-11')
    : (shape === 'wordmark' ? 'h-11 w-[86px]' : 'h-11 w-12');
  const img = size === 'sm'
    ? (shape === 'wordmark' ? 'max-h-7 max-w-[60px]' : 'max-h-8 max-w-8')
    : (shape === 'wordmark' ? 'max-h-8 max-w-[68px]' : 'max-h-9 max-w-9');

  return (
    <div className={`${plate} flex flex-shrink-0 items-center justify-center rounded-xl bg-white px-2.5 shadow-[0_10px_24px_rgba(0,0,0,0.20)]`}>
      <img src={src} alt={alt} className={`${img} h-auto w-auto object-contain`} />
    </div>
  );
};

export default function PaymentMethodsPage() {
  const [methods, setMethods] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState('mode');   // 'mode' | 'details' | 'done'
  const [editing, setEditing] = useState(null);
  const [uploadingQr, setUploadingQr] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [lastCreatedCallback, setLastCreatedCallback] = useState(null);
  const [copiedCallback, setCopiedCallback] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [mRes, gRes] = await Promise.all([
        api.get('/api/admin/payment-methods'),
        api.get('/api/admin/payment-gateways').catch(() => ({ data: [] })),
      ]);
      setMethods(mRes.data.items || mRes.data.methods || []);
      setGateways(Array.isArray(gRes.data) ? gRes.data : (gRes.data?.items || []));
    } catch { toast.error('Failed to load payment methods'); }
    finally { setLoading(false); }
  };

  const linkgenAliasFor = (method) => {
    const candidates = [
      method.gateway_method_code,
      method.gateway_method,
      method.method,
      method.gateway_provider,
      method.provider_code,
      method.title,
    ];
    for (const c of candidates) {
      const key = normalizeGatewayKey(c);
      if (LINKGEN_METHOD_ALIASES.has(key)) return LINKGEN_CALLBACK_ALIASES[key] || key;
    }
    return null;
  };

  const gatewayFor = (method) => {
    const providerCode = method.gateway_provider || method.provider_code;
    const exact = gateways.find(g => g.provider_code === providerCode);
    if (exact) return exact;

    const alias = linkgenAliasFor(method);
    if (!alias) return null;

    const methodKey = normalizeGatewayKey(method.title || method.method || alias);
    return gateways.find((g) =>
      g.provider_code === 'linkgen'
      && normalizeGatewayKey(g.display_name || g.name || '').includes(methodKey)
    ) || gateways.find(g => g.provider_code === 'linkgen') || null;
  };

  const callbackProviderCode = (method) =>
    linkgenAliasFor(method) || method.gateway_provider || method.provider_code;

  const callbackUrl = (providerCode) =>
    `${BACKEND_URL}/api/webhooks/payment-gateway/${providerCode}`;

  // --- Form lifecycle ---
  const openAdd = () => {
    setForm(EMPTY);
    setEditing(null);
    setStep('mode');
    setLastCreatedCallback(null);
    setShowForm(true);
  };

  const openEdit = (m) => {
    const gw = gatewayFor(m);
    const isGateway = m.payment_mode === 'GATEWAY';
    setEditing(m);
    setForm({
      ...EMPTY,
      payment_mode: isGateway ? 'GATEWAY' : 'LOCAL_MANUAL',
      title: m.title || '',
      flow: m.flow || 'both',
      method_details: m.method_details || '',
      bonus_percent: m.bonus_percent || 0,
      fee_percent: m.fee_percent || 0,
      min_amount: m.min_amount || 10,
      max_amount: m.max_amount || 5000,
      receiving_address: m.receiving_address || '',
      admin_note: m.admin_note || '',
      logo_url: m.logo_url || '',
      enabled: m.enabled !== false,
      provider_code: m.gateway_provider || m.provider_code || '',
      gateway_integration_id: gw?.id || null,
      base_url: gw?.base_url || gw?.config?.base_url || '',
      api_key: '',
      api_secret: '',
    });
    setStep('details');
    setLastCreatedCallback(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
    setStep('mode');
  };

  // --- Actions ---
  const handleDelete = async (m) => {
    if (!window.confirm(`Delete payment "${m.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/payment-methods/${m.id}`);
      // If gateway: also delete the linked integration (best-effort)
      if (m.payment_mode === 'GATEWAY') {
        const gw = gatewayFor(m);
        if (gw?.id) {
          await api.delete(`/api/admin/payment-gateways/${gw.id}`).catch(() => {});
        }
      }
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (m) => {
    try {
      const next = !m.enabled;
      await api.post(`/api/admin/payment-methods/${m.id}/toggle?enabled=${next}`);
      toast.success(next ? 'Enabled' : 'Disabled');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const handleTestCallback = async (method) => {
    const gw = gatewayFor(method);
    if (!gw?.id) {
      toast.error('Gateway integration not found for this payment');
      return;
    }
    try {
      const alias = linkgenAliasFor(method);
      const qs = alias ? `?method_alias=${encodeURIComponent(alias)}` : '';
      const res = await api.post(`/api/admin/payment-gateways/${gw.id}/test-callback${qs}`);
      const r = res.data;
      if (r.success) {
        toast.success(`Callback test OK — HTTP ${r.http_status}`);
      } else {
        toast.error(`Callback test failed: ${r.error || 'HTTP ' + r.http_status}`);
      }
      console.log('[Test Callback]', r);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test callback failed');
    }
  };

  // ─────────── Gateway Onboarding Wizard ───────────
  const [wizardFor, setWizardFor] = useState(null);   // integration record
  const [wizardSteps, setWizardSteps] = useState({}); // { step: 'idle'|'running'|'ok'|'fail', result?: any }

  const openWizard = (method) => {
    const gw = gatewayFor(method);
    if (!gw?.id) { toast.error('Gateway integration not found'); return; }
    setWizardFor({ ...gw, __method_alias: linkgenAliasFor(method) });
    setWizardSteps({
      connection: { status: 'idle' },
      callback:   { status: 'idle' },
      discover:   { status: 'idle' },
      sync:       { status: 'idle' },
    });
  };

  const closeWizard = () => { setWizardFor(null); setWizardSteps({}); };

  const runWizardStep = async (step) => {
    if (!wizardFor) return;
    setWizardSteps(p => ({ ...p, [step]: { status: 'running' } }));
    try {
      let res;
      if (step === 'connection') {
        res = await api.post(`/api/admin/payment-gateways/${wizardFor.id}/test-connection`);
        const ok = res.data?.success === true || res.data?.ok === true;
        setWizardSteps(p => ({ ...p, connection: { status: ok ? 'ok' : 'fail', result: res.data } }));
      } else if (step === 'callback') {
        const qs = wizardFor.__method_alias ? `?method_alias=${encodeURIComponent(wizardFor.__method_alias)}` : '';
        res = await api.post(`/api/admin/payment-gateways/${wizardFor.id}/test-callback${qs}`);
        const ok = res.data?.success === true && res.data?.http_status < 500;
        setWizardSteps(p => ({ ...p, callback: { status: ok ? 'ok' : 'fail', result: res.data } }));
      } else if (step === 'discover') {
        res = await api.get(`/api/admin/payment-gateways/${wizardFor.id}/discover-methods`);
        const methods = res.data?.supported_methods
          || res.data?.data?.supported_methods
          || res.data?.methods
          || res.data?.data?.methods
          || [];
        setWizardSteps(p => ({ ...p, discover: { status: methods.length > 0 ? 'ok' : 'fail', result: { count: methods.length, methods } } }));
      } else if (step === 'sync') {
        res = await api.post(
          `/api/admin/payment-gateways/${wizardFor.id}/sync-methods`,
          { enable_imported_methods: true, overwrite_existing_titles: false }
        );
        const imported = res.data?.imported ?? res.data?.data?.imported ?? 0;
        const skipped  = res.data?.skipped  ?? res.data?.data?.skipped  ?? 0;
        setWizardSteps(p => ({ ...p, sync: { status: 'ok', result: { imported, skipped } } }));
        fetchAll();
      }
    } catch (err) {
      setWizardSteps(p => ({ ...p, [step]: { status: 'fail', result: { error: err.response?.data?.detail || err.message } } }));
    }
  };

  const runAllWizardSteps = async () => {
    for (const s of ['connection', 'callback', 'discover', 'sync']) {
      // eslint-disable-next-line no-await-in-loop
      await runWizardStep(s);
    }
  };

  const handleQrUpload = async (methodId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('qr_image', file);
    try {
      setUploadingQr(methodId);
      await api.post(`/api/admin/payment-methods/${methodId}/qr`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('QR uploaded');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QR upload failed');
    } finally { setUploadingQr(null); }
  };

  const triggerFileInput = (methodId) => {
    fileInputRef.current.dataset.methodId = methodId;
    fileInputRef.current.click();
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const methodId = e.target.dataset.methodId;
    if (file && methodId) handleQrUpload(methodId, file);
    e.target.value = '';
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upload_type', 'payment_logo');
      const res = await api.post('/api/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url || res.data.public_url;
      setForm(p => ({ ...p, logo_url: url }));
      toast.success('Logo uploaded');
    } catch { toast.error('Logo upload failed'); }
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const isGateway = form.payment_mode === 'GATEWAY';
    const isWithdrawOnly = form.flow === 'withdraw';
    const isManualWithdrawOnly = !isGateway && isWithdrawOnly;
    try {
      let integrationId = form.gateway_integration_id;
      let providerCode = form.provider_code;

      // 1) Create / update the gateway integration (API mode only)
      if (isGateway) {
        providerCode = (form.provider_code || slugify(form.title)).trim();
        if (!providerCode) {
          toast.error('Please enter a display name so we can derive the provider code');
          return;
        }

        if (editing && integrationId) {
          // update existing integration; only send secrets if user re-entered them
          const patch = {
            display_name: form.title,
            base_url: form.base_url || undefined,
            enabled: form.enabled,
          };
          if (form.api_key) patch.api_key = form.api_key;
          if (form.api_secret) patch.webhook_secret = form.api_secret;
          await api.put(`/api/admin/payment-gateways/${integrationId}`, patch);
        } else {
          // create new integration
          if (!form.api_key || !form.api_secret) {
            toast.error('API key and API secret are required for API mode');
            return;
          }
          const gwRes = await api.post('/api/admin/payment-gateways', {
            provider_code: providerCode,
            display_name: form.title,
            api_key: form.api_key,
            webhook_secret: form.api_secret,
            base_url: form.base_url || undefined,
            enabled: form.enabled,
          });
          integrationId = gwRes.data.id;
        }
      }

      // 2) Create / update the payment method
      const bonusPercent = isWithdrawOnly ? 0 : (Number(form.bonus_percent) || 0);
      const feePercent = Number(form.fee_percent) || 0;
      const manualWithdrawDetails = 'Player provides payout tag, wallet address, phone/email, or QR during withdrawal request.';
      const methodPayload = {
        payment_mode: form.payment_mode,
        title: form.title,
        method: slugify(form.title) || 'manual',
        flow: form.flow,
        method_details: isManualWithdrawOnly
          ? (form.method_details || manualWithdrawDetails)
          : (form.method_details || `Pay via ${form.title}`),
        bonus_percent: bonusPercent,
        fee_percent: feePercent,
        bonus_or_fee: isWithdrawOnly ? feePercent : (bonusPercent || feePercent),
        bonus_fee_direction: isWithdrawOnly ? 'fee' : (feePercent > 0 ? 'fee' : 'bonus'),
        min_amount: Number(form.min_amount) || 0,
        max_amount: Number(form.max_amount) || 0,
        receiving_address: (isGateway || isManualWithdrawOnly) ? '' : form.receiving_address,
        admin_note: form.admin_note,
        logo_url: form.logo_url,
        enabled: form.enabled,
      };
      if (isGateway) {
        methodPayload.gateway_provider = providerCode;
        methodPayload.gateway_method_code = slugify(form.title);
      }

      if (editing) {
        await api.patch(`/api/admin/payment-methods/${editing.id}`, methodPayload);
        toast.success('Payment updated');
        closeForm();
      } else {
        await api.post('/api/admin/payment-methods', methodPayload);
        toast.success('Payment added');
        if (isGateway) {
          setLastCreatedCallback(callbackUrl(providerCode));
          setStep('done');
        } else {
          closeForm();
        }
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Save failed');
    }
  };

  const copyCallback = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCallback(true);
      toast.success('Callback URL copied');
      setTimeout(() => setCopiedCallback(false), 2000);
    } catch { toast.error('Could not copy'); }
  };

  const getQrUrl = (method) => {
    const url = method.qr_code_url || method.qr_code_data || method.qr_image_url;
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="payments-configuration-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Payments Configuration</h2>
          <p className="text-sm text-gray-400">
            {methods.length} payment{methods.length === 1 ? '' : 's'} configured — Manual (QR/wallet) or API (auto wallet load & auto withdraw)
          </p>
        </div>
        <Button onClick={openAdd} className="bg-[#D4AF37] text-black rounded-full" data-testid="add-method-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Payment
        </Button>
      </div>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/png,image/jpeg,image/webp" className="hidden" />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {methods.map((method) => {
          const isReadonly = method.readonly === true;
          const isGateway = method.payment_mode === 'GATEWAY';
          const gw = gatewayFor(method);
          const logoSrc = paymentLogoFor(method);
          return (
            <div
              key={method.id}
              className={`glass-card rounded-xl border ${isReadonly ? 'border-amber-500/30' : isGateway ? 'border-purple-500/30' : 'border-white/10'} overflow-hidden ${!method.enabled ? 'opacity-60' : ''}`}
              data-testid={`method-card-${method.id}`}
            >
              <div className="p-4 border-b border-white/5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {logoSrc ? (
                      <PaymentLogoPlate src={logoSrc} method={method} alt={method.title} />
                    ) : (
                      <div className={`w-10 h-10 rounded-lg ${isGateway ? 'bg-purple-500/20' : 'bg-[#D4AF37]/20'} flex items-center justify-center`}>
                        {isGateway ? <Zap className="w-5 h-5 text-purple-400" /> : <CreditCard className="w-5 h-5 text-[#D4AF37]" />}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{method.title}</h3>
                        {isGateway && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30" data-testid={`api-badge-${method.id}`}>
                            API
                          </span>
                        )}
                        {!isGateway && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
                            MANUAL
                          </span>
                        )}
                        {isReadonly && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`global-badge-${method.id}`}>
                            GLOBAL
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 truncate max-w-[200px]">
                        {isGateway ? (gw?.display_name || gw?.provider_code || method.gateway_provider) : (method.receiving_address || method.method_details || 'No details')}
                      </p>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${method.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {method.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-px bg-white/5">
                <div className="bg-[#0B0E14] p-3 text-center">
                  <p className="text-[10px] text-gray-500">Bonus</p>
                  <p className="font-semibold text-green-400">{method.bonus_percent || 0}%</p>
                </div>
                <div className="bg-[#0B0E14] p-3 text-center">
                  <p className="text-[10px] text-gray-500">Fee</p>
                  <p className="font-semibold text-orange-400">{method.fee_percent || 0}%</p>
                </div>
                <div className="bg-[#0B0E14] p-3 text-center">
                  <p className="text-[10px] text-gray-500">Min</p>
                  <p className="font-semibold">${method.min_amount || 0}</p>
                </div>
                <div className="bg-[#0B0E14] p-3 text-center">
                  <p className="text-[10px] text-gray-500">Max</p>
                  <p className="font-semibold">${method.max_amount || 0}</p>
                </div>
              </div>

              {/* Mode-specific body */}
              {isGateway ? (
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Globe className="w-3 h-3" /> <code className="text-gray-300 truncate">{gw?.base_url || gw?.config?.base_url || '—'}</code>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Key className="w-3 h-3" /> <code className="text-gray-300 font-mono">{gw?.config?.api_key_masked || '••••'}</code>
                  </div>
                  <div className="p-2 rounded-lg bg-black/30 border border-white/5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider">Callback URL</p>
                      <code className="text-[10px] text-purple-300 block truncate">{callbackUrl(callbackProviderCode(method))}</code>
                    </div>
                    <button
                        onClick={() => copyCallback(callbackUrl(callbackProviderCode(method)))}
                        className="p-1.5 rounded-md hover:bg-white/5 text-purple-300 flex-shrink-0"
                        data-testid={`copy-callback-${method.id}`}
                        title="Copy callback URL"
                      >
                        {copiedCallback ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  {method.flow === 'withdraw' ? (
                    <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3">
                      <p className="text-xs font-semibold text-green-300">Withdrawal payout method</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-gray-400">
                        No admin receiving QR is needed. Players submit their tag, address, phone/email, or QR inside each withdrawal request.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <QrCode className="w-3 h-3" /> QR / Payment Image
                        </span>
                        <Button
                          onClick={() => triggerFileInput(method.id)}
                          disabled={uploadingQr === method.id || isReadonly}
                          variant="outline" size="sm"
                          className="h-7 text-xs border-white/10"
                          data-testid={`upload-qr-${method.id}`}
                        >
                          {uploadingQr === method.id ? <div className="spinner w-3 h-3" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
                        </Button>
                      </div>
                      {getQrUrl(method) ? (
                        <img src={getQrUrl(method)} alt={`${method.title} QR`} className="w-full h-32 object-contain bg-white rounded-lg" />
                      ) : (
                        <div className="w-full h-24 bg-[#131822] rounded-lg flex items-center justify-center text-gray-600 border border-dashed border-white/10">
                          <div className="text-center">
                            <ImageIcon className="w-5 h-5 mx-auto mb-1 opacity-50" />
                            <p className="text-[10px]">Upload QR for Chime/Venmo/CashApp</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="px-4 pb-4 flex items-center gap-2">
                <Button
                  onClick={() => openEdit(method)}
                  variant="outline" size="sm"
                  className="flex-1 border-white/10 text-xs rounded-full"
                  disabled={isReadonly}
                  data-testid={`edit-method-${method.id}`}
                >
                  <Edit2 className="w-3 h-3 mr-1" /> {isReadonly ? 'View Only' : 'Edit'}
                </Button>
                {isGateway && !isReadonly && (
                  <>
                    <Button
                      onClick={() => handleTestCallback(method)}
                      variant="outline" size="sm"
                      className="border-purple-500/30 text-purple-300 text-xs rounded-full"
                      data-testid={`test-callback-${method.id}`}
                      title="Send a signed test webhook to validate wiring"
                    >
                      <Zap className="w-3 h-3 mr-1" /> Test
                    </Button>
                    <Button
                      onClick={() => openWizard(method)}
                      variant="outline" size="sm"
                      className="border-amber-500/30 text-amber-300 text-xs rounded-full"
                      data-testid={`wizard-${method.id}`}
                      title="Run the 4-step onboarding wizard"
                    >
                      <Sparkles className="w-3 h-3 mr-1" /> Wizard
                    </Button>
                  </>
                )}
                <button
                  onClick={() => !isReadonly && handleToggle(method)}
                  disabled={isReadonly}
                  className={`p-2 rounded-lg hover:bg-white/5 ${method.enabled ? 'text-green-400' : 'text-gray-400'} ${isReadonly ? 'opacity-30 cursor-not-allowed' : ''}`}
                  data-testid={`toggle-method-${method.id}`}
                >
                  <Power className="w-4 h-4" />
                </button>
                <button
                  onClick={() => !isReadonly && handleDelete(method)}
                  disabled={isReadonly}
                  className={`p-2 rounded-lg hover:bg-white/5 text-red-400 ${isReadonly ? 'opacity-30 cursor-not-allowed' : ''}`}
                  data-testid={`delete-method-${method.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {methods.length === 0 && (
        <div className="text-center py-12 glass-card rounded-xl border border-white/10">
          <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400 mb-4">No payments configured yet.</p>
          <Button onClick={openAdd} className="bg-[#D4AF37] text-black rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Payment
          </Button>
        </div>
      )}

      {/* ─────────── Modal ─────────── */}
      <Modal
        open={showForm}
        onClose={closeForm}
        title={editing ? 'Edit Payment' : step === 'mode' ? 'Add Payment — Choose Type' : step === 'done' ? 'Payment Added' : 'Add Payment'}
        maxWidth="max-w-lg"
      >
        {/* STEP 1: Mode selection */}
        {step === 'mode' && !editing && (
          <div className="space-y-3" data-testid="add-mode-step">
            <p className="text-sm text-gray-400 mb-4">How will players pay for this payment method?</p>
            <button
              type="button"
              onClick={() => { setForm(p => ({ ...p, payment_mode: 'LOCAL_MANUAL' })); setStep('details'); }}
              className="w-full p-4 rounded-xl border border-white/10 hover:border-[#D4AF37]/50 hover:bg-white/5 transition-all text-left group"
              data-testid="select-manual-mode"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-sky-500/15 flex items-center justify-center flex-shrink-0">
                  <QrCode className="w-6 h-6 text-sky-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold mb-1 flex items-center gap-2">
                    Manual / QR <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">Quick setup</span>
                  </h4>
                  <p className="text-xs text-gray-400">
                    Show a wallet address, tag, QR code & logo. Player sends payment, uploads proof — you approve manually.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => { setForm(p => ({ ...p, payment_mode: 'GATEWAY' })); setStep('details'); }}
              className="w-full p-4 rounded-xl border border-white/10 hover:border-purple-500/50 hover:bg-white/5 transition-all text-left group"
              data-testid="select-api-mode"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold mb-1 flex items-center gap-2">
                    API Gateway <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300">Auto-deposit</span>
                  </h4>
                  <p className="text-xs text-gray-400">
                    Connect your payment API (base URL + API key + API secret). Players get a pay link, we auto-load wallet on confirmation & auto-payout withdraws.
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* STEP 2: Details form */}
        {step === 'details' && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-details-form">
            {!editing && (
              <div className="flex items-center gap-2 text-xs text-gray-400 pb-2 border-b border-white/5">
                {form.payment_mode === 'GATEWAY'
                  ? <><Zap className="w-3.5 h-3.5 text-purple-400" /> <span className="text-purple-300">API Gateway</span></>
                  : <><QrCode className="w-3.5 h-3.5 text-sky-400" /> <span className="text-sky-300">Manual / QR</span></>}
                <button type="button" onClick={() => setStep('mode')} className="ml-auto text-[#D4AF37] hover:underline">Change</button>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-400 mb-1 block">Display Name *</label>
              <Input
                value={form.title}
                onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                required
                className="bg-[#0B0E14] border-white/10"
                placeholder={form.payment_mode === 'GATEWAY' ? 'e.g. Stripe, 1TapPay' : 'e.g. Cash App, Venmo, Chime'}
                data-testid="method-title"
              />
            </div>

            {/* Logo (both modes) */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Payment Logo</label>
              <input type="file" ref={logoInputRef} accept="image/*" className="hidden" onChange={handleLogoUpload} />
              <div className="flex items-center gap-3">
                {form.logo_url && (
                  <PaymentLogoPlate
                    src={resolveStoredLogo(form.logo_url)}
                    method={{ title: form.title, provider_code: form.provider_code }}
                    alt="Logo"
                    size="sm"
                  />
                )}
                {!form.logo_url && resolvePaymentBrandLogo({ title: form.title, provider_code: form.provider_code }) && (
                  <PaymentLogoPlate
                    src={resolvePaymentBrandLogo({ title: form.title, provider_code: form.provider_code })}
                    method={{ title: form.title, provider_code: form.provider_code }}
                    alt="Suggested logo"
                    size="sm"
                  />
                )}
                <Button type="button" onClick={() => logoInputRef.current?.click()} variant="outline" className="border-white/10 text-xs rounded-full">
                  <Upload className="w-3 h-3 mr-1" /> {form.logo_url ? 'Change' : 'Upload'}
                </Button>
                {form.logo_url && <button type="button" onClick={() => setForm(p => ({ ...p, logo_url: '' }))} className="text-red-400 text-xs">Remove</button>}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-1 block">Flow</label>
              <select
                value={form.flow}
                onChange={(e) => setForm(p => ({ ...p, flow: e.target.value }))}
                className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white"
              >
                <option value="both">Both (Deposit & Withdraw)</option>
                <option value="deposit">Deposit Only</option>
                <option value="withdraw">Withdrawal Only</option>
              </select>
            </div>

            {/* ---------- MANUAL fields ---------- */}
            {form.payment_mode === 'LOCAL_MANUAL' && (
              <>
                {form.flow === 'withdraw' ? (
                  <div
                    className="rounded-xl border border-green-500/25 bg-green-500/5 p-3"
                    data-testid="withdraw-method-info"
                  >
                    <p className="text-sm font-semibold text-green-300">Withdrawal destination is entered by the player.</p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-400">
                      For withdrawal-only methods, admin only configures the method name, logo, limits, and optional instructions. The player submits their Chime tag, wallet address, Apple/Google Pay details, or QR when they request withdrawal.
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Receiving Wallet / Address / Tag *</label>
                    <Input
                      value={form.receiving_address}
                      onChange={(e) => setForm(p => ({ ...p, receiving_address: e.target.value }))}
                      className="bg-[#0B0E14] border-white/10 font-mono"
                      placeholder="$YourCashTag, @VenmoHandle, 0xABC…"
                      data-testid="method-receiving-address"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    {form.flow === 'withdraw' ? 'Withdrawal Instructions (optional)' : 'Instructions / Note Shown to Player'}
                  </label>
                  <Input
                    value={form.method_details}
                    onChange={(e) => setForm(p => ({ ...p, method_details: e.target.value }))}
                    className="bg-[#0B0E14] border-white/10"
                    placeholder={form.flow === 'withdraw'
                      ? 'e.g. Upload your QR or enter the tag/address where you want payout'
                      : 'e.g. Send to $CashTag, include your username'}
                  />
                </div>
                {editing && (
                  <p className="text-[10px] text-gray-500">
                    Upload/replace the QR image from the card after saving.
                  </p>
                )}
              </>
            )}

            {/* ---------- GATEWAY fields ---------- */}
            {form.payment_mode === 'GATEWAY' && (
              <>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">API Base URL *</label>
                  <Input
                    value={form.base_url}
                    onChange={(e) => setForm(p => ({ ...p, base_url: e.target.value }))}
                    placeholder="https://api.provider.com/v1"
                    className="bg-[#0B0E14] border-white/10 font-mono"
                    data-testid="method-base-url"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    API Key {editing && <span className="text-gray-600">(leave blank to keep current)</span>}
                  </label>
                  <Input
                    type="password"
                    value={form.api_key}
                    onChange={(e) => setForm(p => ({ ...p, api_key: e.target.value }))}
                    placeholder={editing ? '•••• leave blank ••••' : 'sk_live_…'}
                    className="bg-[#0B0E14] border-white/10 font-mono"
                    data-testid="method-api-key"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    API Secret / Webhook Secret {editing && <span className="text-gray-600">(leave blank to keep current)</span>}
                  </label>
                  <Input
                    type="password"
                    value={form.api_secret}
                    onChange={(e) => setForm(p => ({ ...p, api_secret: e.target.value }))}
                    placeholder={editing ? '•••• leave blank ••••' : 'whsec_…'}
                    className="bg-[#0B0E14] border-white/10 font-mono"
                    data-testid="method-api-secret"
                  />
                </div>

                {/* Callback URL preview */}
                <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Link2 className="w-3.5 h-3.5 text-purple-300" />
                    <p className="text-[11px] font-semibold text-purple-300">Your Callback URL</p>
                  </div>
                  <p className="text-[10px] text-gray-400 mb-2">
                    Add this URL in your provider's webhook settings so we know when to load wallets and finalize withdrawals.
                  </p>
                  <div className="flex items-center gap-2 bg-black/30 rounded-md p-2">
                    <code className="text-[11px] text-purple-200 flex-1 truncate break-all">
                      {callbackUrl(form.provider_code || slugify(form.title) || 'your_code')}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyCallback(callbackUrl(form.provider_code || slugify(form.title) || 'your_code'))}
                      className="p-1 rounded hover:bg-white/5 text-purple-300"
                    >
                      {copiedCallback ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Limits + bonus/fee */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Bonus %</label>
                <Input type="number" value={form.bonus_percent || ''} onChange={(e) => setForm(p => ({ ...p, bonus_percent: e.target.value === '' ? 0 : parseFloat(e.target.value) }))} placeholder="0" className="bg-[#0B0E14] border-white/10" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Fee %</label>
                <Input type="number" value={form.fee_percent || ''} onChange={(e) => setForm(p => ({ ...p, fee_percent: e.target.value === '' ? 0 : parseFloat(e.target.value) }))} placeholder="0" className="bg-[#0B0E14] border-white/10" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Min Amount</label>
                <Input type="number" value={form.min_amount || ''} onChange={(e) => setForm(p => ({ ...p, min_amount: e.target.value === '' ? 0 : parseFloat(e.target.value) }))} placeholder="10" className="bg-[#0B0E14] border-white/10" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Max Amount</label>
                <Input type="number" value={form.max_amount || ''} onChange={(e) => setForm(p => ({ ...p, max_amount: e.target.value === '' ? 0 : parseFloat(e.target.value) }))} placeholder="5000" className="bg-[#0B0E14] border-white/10" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-1 block">Admin Note (shown on deposit popup)</label>
              <Input value={form.admin_note} onChange={(e) => setForm(p => ({ ...p, admin_note: e.target.value }))} className="bg-[#0B0E14] border-white/10" placeholder="Any important note for players" />
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm(p => ({ ...p, enabled: e.target.checked }))}
                className="w-4 h-4 rounded accent-[#D4AF37]"
              />
              Enabled and visible to players
            </label>

            <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
              <Button type="button" onClick={closeForm} variant="outline" className="border-white/10 rounded-full">Cancel</Button>
              <Button type="submit" className="bg-[#D4AF37] text-black rounded-full" data-testid="method-submit">
                {editing ? 'Save Changes' : form.payment_mode === 'GATEWAY' ? 'Create API Payment' : 'Create Payment'}
              </Button>
            </div>
          </form>
        )}

        {/* STEP 3: Done — show callback URL */}
        {step === 'done' && lastCreatedCallback && (
          <div className="space-y-5 text-center py-4" data-testid="add-done-step">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">API payment added!</h3>
              <p className="text-sm text-gray-400 mt-1">
                Configure the callback URL below in your provider's dashboard so we can auto-load wallets and finalize withdrawals.
              </p>
            </div>

            <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 text-left">
              <div className="flex items-center gap-2 mb-1.5">
                <Link2 className="w-3.5 h-3.5 text-purple-300" />
                <p className="text-[11px] font-semibold text-purple-300">Webhook / Callback URL</p>
              </div>
              <div className="flex items-center gap-2 bg-black/30 rounded-md p-2">
                <code className="text-[11px] text-purple-200 flex-1 break-all">{lastCreatedCallback}</code>
                <button
                  type="button"
                  onClick={() => copyCallback(lastCreatedCallback)}
                  className="p-1 rounded hover:bg-white/5 text-purple-300 flex-shrink-0"
                >
                  {copiedCallback ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <Button onClick={closeForm} className="bg-[#D4AF37] text-black rounded-full w-full">Done</Button>
          </div>
        )}
      </Modal>

      {/* ─────────── Gateway Onboarding Wizard ─────────── */}
      <Modal
        open={!!wizardFor}
        onClose={closeWizard}
        title={wizardFor ? `Onboarding: ${wizardFor.display_name || wizardFor.provider_code}` : 'Onboarding Wizard'}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4" data-testid="gateway-onboarding-wizard">
          <p className="text-sm text-gray-400">
            Runs 4 checks against your provider API. Use this right after adding the gateway to validate the connection, webhook signature, method discovery, and sync before going live.
          </p>

          {[
            { id: 'connection', label: 'Test Connection', desc: 'Verifies API base URL + API key reach the provider and return healthy.', icon: Globe },
            { id: 'callback',   label: 'Test Callback',   desc: 'Sends an HMAC-signed dummy webhook to your callback URL; confirms wiring is correct.', icon: Link2 },
            { id: 'discover',   label: 'Discover Methods', desc: 'Asks the provider which payment methods are currently enabled on its end.', icon: Key },
            { id: 'sync',       label: 'Sync Methods',    desc: 'Imports the discovered methods into Payments Configuration (enabled by default).', icon: Sparkles },
          ].map((s) => {
            const step = wizardSteps[s.id] || { status: 'idle' };
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                  step.status === 'ok'      ? 'border-green-500/30 bg-green-500/5' :
                  step.status === 'fail'    ? 'border-red-500/30 bg-red-500/5' :
                  step.status === 'running' ? 'border-amber-500/30 bg-amber-500/5' :
                                              'border-white/10 bg-white/3'
                }`}
                data-testid={`wizard-step-${s.id}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  step.status === 'ok'      ? 'bg-green-500/15 text-green-400' :
                  step.status === 'fail'    ? 'bg-red-500/15 text-red-400' :
                  step.status === 'running' ? 'bg-amber-500/15 text-amber-300' :
                                              'bg-white/5 text-gray-400'
                }`}>
                  {step.status === 'ok'      ? <CheckCircle2 className="w-4 h-4" /> :
                   step.status === 'fail'    ? <XCircle className="w-4 h-4" /> :
                   step.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                                               <Icon className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm">{s.label}</p>
                    <Button
                      onClick={() => runWizardStep(s.id)}
                      variant="outline"
                      size="sm"
                      className="h-7 border-white/10 text-[10px] rounded-full"
                      disabled={step.status === 'running'}
                      data-testid={`wizard-run-${s.id}`}
                    >
                      {step.status === 'ok' ? 'Re-run' : step.status === 'running' ? 'Running…' : 'Run'}
                    </Button>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">{s.desc}</p>
                  {step.result && step.status !== 'running' && (
                    <div className="mt-2 p-2 rounded-md bg-black/30 border border-white/5">
                      {s.id === 'connection' && (
                        <p className="text-[11px]">
                          {step.status === 'ok'
                            ? `✅ OK${step.result?.response_ms ? ` (${step.result.response_ms}ms)` : ''}`
                            : `❌ ${step.result?.error || step.result?.message || 'Failed'}`}
                        </p>
                      )}
                      {s.id === 'callback' && (
                        <p className="text-[11px]">
                          {step.status === 'ok'
                            ? `✅ HTTP ${step.result?.http_status} — HMAC wiring valid`
                            : `❌ ${step.result?.error || `HTTP ${step.result?.http_status}`}`}
                        </p>
                      )}
                      {s.id === 'discover' && (
                        <p className="text-[11px]">
                          {step.status === 'ok'
                            ? `✅ Found ${step.result?.count} method${step.result?.count === 1 ? '' : 's'}: ${(step.result?.methods || []).map(m => m.method || m.code).join(', ')}`
                            : `❌ ${step.result?.error || 'No methods discovered'}`}
                        </p>
                      )}
                      {s.id === 'sync' && (
                        <p className="text-[11px]">
                          {step.status === 'ok'
                            ? `✅ Imported ${step.result?.imported}, skipped ${step.result?.skipped}`
                            : `❌ ${step.result?.error || 'Sync failed'}`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex justify-between gap-2 pt-2 border-t border-white/10">
            <Button
              onClick={closeWizard}
              variant="outline"
              className="border-white/10 rounded-full"
              data-testid="wizard-close"
            >
              Close
            </Button>
            <Button
              onClick={runAllWizardSteps}
              className="bg-[#D4AF37] text-black rounded-full"
              data-testid="wizard-run-all"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Run All Steps
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
